import React, { useCallback, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, AlertTitle, Box, Button, Grid, Paper, Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoltLightning, faFileInvoice } from '@fortawesome/free-solid-svg-icons';
import api from '../../../api/req';
import { checkTaskPromise } from '../../../api/checktask';
import { EditorControls } from '../../BaseEditor';
import Loader from '../../../components/MaterialUI/loader';
import { AppContext } from '../../../providers/authProvider';
import { getFrontEndByBackend } from '../../../const/meta';

function ContractsReloadEditor() {
  const {
    auth,
    currentUser,
  } = useContext(AppContext);
  const [dates, setDates] = useState(() => {
    const d = new Date();
    return {
      bdate: `${d.getFullYear()}-01-01`,
      edate: d.toISOString()
        .slice(0, 10),
    };
  });
  const [org, setOrg] = useState(() => currentUser.org);
  const [kdb, setKdb] = useState(null);
  const [payer, setPayer] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updatedDocuments, setUpdatedDocuments] = useState([]);
  const [done, setDone] = useState(false);

  const startProcessing = useCallback(() => {
    const loader = async () => {
      const options = {
        bdate: dates.bdate,
        edate: dates.edate,
        org: org.id,
        ...kdb ? { kdb: kdb.id } : {},
        ...payer ? { payer: payer.id } : {},
      };
      const r = await api.post$('/api/service/reloadcontracts/', auth, options);
      const d = await r.json();
      return checkTaskPromise(d.task_id, auth);
    };
    const getDocs = async (backend, ids) => {
      if (!ids.length) return [];
      const r = await api.get(backend, auth, { id_in: String(ids) });
      if (r.ok) {
        const data = await r.json();
        const frontend = getFrontEndByBackend(backend);
        return data.results.map((d) => ({
          id: d.id,
          repr: d.repr,
          url: `${frontend}${d.id}/`,
        }));
      }
      throw new Error(`${r.status} ${r.statusText}`);
    };
    const resultParser = async (result) => {
      const rslts = await Promise.allSettled(
        Object.values(result.result.updated)
          .map((rr) => getDocs(rr.uri, rr.items)),
      );
      const errors = rslts.filter((r) => r.status === 'rejected')
        .map((r) => r.reason);
      if (errors.length) throw new Error(`Помилка пошуку оброблених документів: ${errors.join(', ')}`);
      return rslts.reduce((R, rr) => [...R, ...rr.value], []);
    };
    setLoading(true);
    setErr(null);
    setUpdatedDocuments([]);
    setDone(false);
    loader()
      .then((data) => resultParser(data))
      .then((docs) => {
        setUpdatedDocuments(docs);
        setDone(true);
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [auth, dates.bdate, dates.edate, kdb, org.id, payer]);

  return (
    <Paper sx={{
      p: 2, boxSizing: 'border-box', height: 1, overflow: 'auto',
    }}
    >
      {err && (
        <Alert severity="error">{err}</Alert>
      )}
      {loading && (
        <Loader text="Завантаження" />
      )}
      <Typography variant="h4" color="primary">Перезаповнити контракти</Typography>
      <Grid container spacing={2}>
        <Grid item sm={3}>
          <EditorControls.DateInput
            label="Дата початку"
            value={dates.bdate}
            onChange={(e, v) => setDates((od) => ({
              ...od,
              bdate: v,
            }))}
          />
        </Grid>
        <Grid item sm={3}>
          <EditorControls.DateInput
            label="Дата закінчення"
            value={dates.edate}
            onChange={(e, v) => setDates((od) => ({
              ...od,
              edate: v,
            }))}
          />
        </Grid>
        {currentUser.is_superuser && (
          <Grid item sm={6}>
            <EditorControls.ItemPicker
              label="Установа"
              value={org}
              backend="/api/catalog/orgs/"
              onChange={(e, v) => {
                setOrg(v);
                setPayer(null);
              }}
            />
          </Grid>
        )}
        <Grid item sm={6}>
          <EditorControls.ItemPicker
            label="Обробляти документи тільки по вказаному КДБ"
            value={kdb}
            backend="/api/catalog/kdbs/"
            onChange={(e, v) => setKdb(v)}
            canErase
          />
        </Grid>
        <Grid item sm={6}>
          <EditorControls.ItemPicker
            label="Обробляти документи тільки по вказаному платнику"
            value={payer}
            backend="/api/catalog/payers/"
            onChange={(e, v) => setPayer(v)}
            filter={org ? { org: org.id } : {}}
            canErase
          />
        </Grid>
      </Grid>
      <Button
        startIcon={<FontAwesomeIcon icon={faBoltLightning} />}
        color="success"
        variant="contained"
        onClick={startProcessing}
        disabled={loading}
      >
        Виконати
      </Button>
      <hr />
      {done && !!updatedDocuments.length && (
        <>
          <Typography variant="h6" color="primary">Оброблено наступні документи:</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {updatedDocuments.map((doc) => (
              <Button
                key={doc.url}
                variant="outlined"
                startIcon={<FontAwesomeIcon icon={faFileInvoice} />}
                component={Link}
                to={doc.url}
              >
                {doc.repr}
              </Button>
            ))}
          </Box>
        </>
      )}
      {done && !updatedDocuments.length && (
        <Alert severity="warning">
          <AlertTitle>Не було оброблено жодного документу.</AlertTitle>
          Перевірте параметри обробки.
        </Alert>
      )}
    </Paper>
  );
}

export default ContractsReloadEditor;
